// F1 — **THE FUNNEL LANDING PAGE: ONE PAGE, THREE DOORS, PHONE FIRST.**
//
// Her order: a standalone page not reachable from inside the site, wearing the HOME PAGE BACKGROUND
// with GOLD CHROME lettering, beautiful and inviting, serving all three funnels through a parameter,
// **phone-first because the traffic is Instagram.**
//
// **IT IS HOOKLESS ON PURPOSE, SO A GATE CALLS IT** (R7/R12's entry requirement). This is the first
// thing a stranger who cost her money ever sees, and a component holding state inside App.tsx could
// only ever be checked by a regex over a twelve-thousand-line file. It takes the funnel key as a
// PROP — the query string is read by the router, which already owns that job — so every one of the
// three doors is a pure function a fixture renders and reads back.
//
// **WHAT IS DELIBERATELY NOT HERE YET, NAMED RATHER THAN LEFT AS AN ABSENCE (R17/R160):** the CAPTURE
// (F2), the free READING (F3), the LEAD row (F4) and the rate fence (F6). Her clause 6 is that this
// can never become a free-reading firehose, and a generation door shipped ahead of its
// **SERVER-SIDE** fence is exactly that firehose — so this push carries **no form and no engine call
// at all**, which makes the firehose impossible by construction rather than by a guard written next.
// The three doors are live for her ads today; the capture arrives with its fence in the same push.
import { FUNNEL_KEYS, funnelCopy, funnelFromParam } from "../funnels.js";

export type FunnelProps = {
  /** The raw `?f=` as it arrived. Unrostered or absent resolves through the law, never here. */
  funnel?: string | null;
  /**
   * F2 — **THE CAPTURE, COMPOSED IN AS A NODE RATHER THAN RENDERED HERE.**
   *
   * A form holds state, and the moment this page held state it would stop being CALLABLE — which is
   * the only reason a gate can render all three doors and read the tree back. `HomeShowcase`'s
   * pattern exactly: the stateful thing is passed in, and the page stays a pure function of props.
   */
  capture?: unknown;
};

export function FunnelPage({ funnel, capture }: FunnelProps) {
  const key = funnelFromParam(funnel);
  const copy = funnelCopy(key);
  return (
    // R43: **a `<section>` and NOT a `<main>`.** The whole app already lives inside one `<main>` in
    // App.tsx, and a second landmark is a document outline a screen reader cannot navigate — which
    // R43's census refuses by name. The page's own `<h1>` below is what names it in that outline.
    <section className="funnel" data-funnel={key}>
      <div className="funnel__inner">
        {/* R43/R44: the page's title in its document outline. It is the HOOK, because on this page
            the headline really is the heading — unlike the tab title, which serves all three. */}
        <h1 className="funnel__hook">{copy.title}</h1>
        {/* **L2 (her live-test order, 2026-08-13) — THE SUBTITLE IS REMOVED ENTIRELY.** Her words:
            *"the subtitle line 'Your chart carries a money story…' is REMOVED entirely — the title
            and the form carry the page."* It is deleted at its DECLARATION (DL-2/DT-2b/R181) rather
            than commented out or hidden by a rule: a retired line still sitting in `FUNNEL_COPY` is
            one render away from coming back, and the fixture clauses that held it are migrated in
            the same push rather than left to go red about a page behaving exactly as she ordered. */}
        {/* F2 — the capture. **The fallback is not decoration:** a page that renders a headline with
            nothing under it is a paid click arriving at a dead stop, so if the capture is ever not
            supplied the visitor is still told what happens next rather than shown a wall. */}
        {capture ? <div className="funnel__capture">{capture as never}</div>
          : <p className="funnel__soon">Your free reading opens here in a moment.</p>}
      </div>
    </section>
  );
}

/** Exported so a fixture can drive every door without knowing the roster twice (R28/R29). */
export const FUNNEL_DOORS = FUNNEL_KEYS;
